import {z} from 'zod'

/**
 * Public, read-only permit contract served by the external `/api/v1` API
 * (consumed by the sister product, Mongi). This is the SINGLE SOURCE OF TRUTH
 * for the external permit shape and is deliberately decoupled from the Prisma
 * `Permit` model: only the explicitly-listed public fields cross the boundary.
 *
 * Defined as a Zod schema so one definition drives three things: the TypeScript
 * type (`z.infer`, used by the allowlist mapper's `: PermitDTO` guard), and the
 * OpenAPI response schema (`z.toJSONSchema`, served at `/api/v1/openapi.json`).
 * That keeps the published docs from drifting away from the actual contract.
 *
 * Internal/bookkeeping columns (e.g. `rawSourceId`, `metadata`) and everything
 * outside the permit layer (workflow, organization, assignment, sprint state)
 * must never appear here. Changing the Prisma model does NOT change this
 * contract unless the mapper is updated to match — see
 * `packages/api/src/routes/v1/permits/service/map-permit-to-dto.ts`.
 *
 * All timestamps are ISO-8601 (RFC 3339) strings in UTC.
 */

/**
 * One contractor associated with a permit (a general contractor or a sub).
 * Array order is meaningful: rows are sorted by the source `position`, so
 * index 0 is the conventional "primary" contractor. Internal `position` /
 * `normalizedKey` columns are not exposed.
 */
export const permitContractorDtoSchema = z.object({
	/** Contractor name as sourced from the municipality (internal `rawName`). */
	name: z.string(),
	phone: z.string().nullable(),
	address: z.string().nullable(),
	/** e.g. "General Contractor", "Electrical" — null when the source omits it. */
	role: z.string().nullable(),
})

export const permitDtoSchema = z.object({
	/** Stable primary identifier. Use for de-duplication on the consumer side. */
	id: z.string(),
	permitNumber: z.string().nullable(),
	region: z.string(),
	address: z.string().nullable(),
	typeOfWork: z.string().nullable(),
	/** Municipal filed valuation, as an integer number of CAD dollars. */
	projectValue: z.number().int().nullable(),
	projectDescription: z.string().nullable(),
	applicant: z.string().nullable(),
	applicantPhone: z.string().nullable(),
	/** Date the municipality issued the permit (UTC ISO-8601), or null. */
	issueDate: z.iso.datetime().nullable(),
	/**
	 * When Redstack first scraped/created the record (UTC ISO-8601). This is the
	 * field the `?since=` watermark filters on — i.e. a created-since feed.
	 */
	createdAt: z.iso.datetime(),
	/**
	 * When the Permit row was last written (UTC ISO-8601). NOTE: this does not
	 * bump on contractor-table changes, so it is not a reliable change-feed
	 * signal for edits to already-returned permits.
	 */
	updatedAt: z.iso.datetime(),
	contractors: z.array(permitContractorDtoSchema),
})

export type PermitContractorDTO = z.infer<typeof permitContractorDtoSchema>
export type PermitDTO = z.infer<typeof permitDtoSchema>
